import { aiUsageFetch } from "../../lib/aiUsageApi";
import { SATIN_API } from "../../config/stitchServices";
import { loadSatinJob, saveSatinJob } from "./satinJobStorage";
import {
  samplePath,
  splitAtParams,
  alignRailDirections,
} from "../SvgEditor/helpers/splitPathToRails";
import { satinRailQuality } from "./satinRailQuality";
import { inside } from "./autoSatinGeometry";
import { sideRailCandidates } from "./sideRailCandidates";
import { improveSatinGuides } from "./satinDirectionGuides";

function distanceToBoundary(q, ring) {
  let best = Infinity;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i],
      b = ring[(i + 1) % ring.length],
      dx = b.x - a.x,
      dy = b.y - a.y;
    const t = Math.max(
      0,
      Math.min(
        1,
        ((q.x - a.x) * dx + (q.y - a.y) * dy) / (dx * dx + dy * dy || 1)
      )
    );
    best = Math.min(best, Math.hypot(q.x - a.x - t * dx, q.y - a.y - t * dy));
  }
  return best;
}
export function isCollapsedSatinPiece(piece) {
  const points = piece.contours?.flat() || [];
  const hasNoExtent = Boolean(
    points.length &&
      points.every(([x, y]) => Number.isFinite(x) && Number.isFinite(y)) &&
      Math.max(...points.map((p) => p[0])) -
        Math.min(...points.map((p) => p[0])) <
        1e-8 &&
      Math.max(...points.map((p) => p[1])) -
        Math.min(...points.map((p) => p[1])) <
        1e-8
  );
  if (hasNoExtent) return true;

  // Polygonization can also leave a short, almost-collinear triangle at a cut
  // intersection. It has visible length but effectively no stitchable area, so
  // asking the rail helper to turn it into two sides can only fail. Compare area
  // with perimeter squared to keep this independent of canvas scale.
  const ring = piece.contours?.[0] || [];
  if (ring.length < 3) return true;
  let twiceArea = 0;
  let perimeter = 0;
  for (let i = 0; i < ring.length; i++) {
    const [x0, y0] = ring[i];
    const [x1, y1] = ring[(i + 1) % ring.length];
    if (![x0, y0, x1, y1].every(Number.isFinite)) return false;
    twiceArea += x0 * y1 - x1 * y0;
    perimeter += Math.hypot(x1 - x0, y1 - y0);
  }
  return perimeter > 0 && Math.abs(twiceArea) / 2 / perimeter ** 2 < 1e-5;
}
export function materializeVideoRails(result) {
  const pairs = [];
  for (const piece of result.pieces || []) {
    // Polygonization can create floating-point dust at intersecting cuts. The
    // backend serializes paths to 8 decimal places; a piece with no extent at
    // that precision cannot form a rail. Also covers previously saved jobs.
    if (isCollapsedSatinPiece(piece)) continue;
    const rings = piece.contours.map((r) => r.map(([x, y]) => ({ x, y })));
    const contains = (q) =>
      (inside(q, rings[0]) || distanceToBoundary(q, rings[0]) <= 0.12) &&
      !rings
        .slice(1)
        .some((r) => inside(q, r) && distanceToBoundary(q, r) > 0.12);
    const valid = (pair) =>
      pair?.rungs?.length &&
      pair.rungs.every((d) => {
        const pts = samplePath(d, 12);
        return pts.slice(1, -1).every(contains);
      });
    if (piece.existingRails) {
      if (!valid(piece.existingRails))
        throw new Error(`Guides in section ${piece.id} need manual review.`);
      pairs.push({
        ...piece.existingRails,
        method: "select-existing-contours",
      });
      continue;
    }
    const samples = samplePath(piece.path, 300);
    let chosen;
    let bestQuality = Infinity;
    for (const sides of sideRailCandidates(piece.contours)) {
      const pair = alignRailDirections(sides.rail0, sides.rail1, 18);
      const quality = satinRailQuality(
        samplePath(pair.rail0, 40),
        samplePath(pair.rail1, 40)
      );
      if (quality !== null && quality < bestQuality && valid(pair)) {
        bestQuality = quality;
        chosen = {
          ...pair,
          method: "opposing-side-rails",
          piecePath: piece.path,
        };
      }
    }
    // Prefer validated four-endpoint rails. Two-point splitting remains a
    // fallback for tapered shapes without two identifiable end caps.
    if (chosen) {
      pairs.push(improveSatinGuides(chosen, valid));
      continue;
    }
    for (const { t1, t2 } of piece.candidates) {
      const split = splitAtParams(samples, t1, t2);
      if (!split) continue;
      const pair = alignRailDirections(split.rail0, split.rail1, 18);
      const quality = satinRailQuality(
        samplePath(pair.rail0, 40),
        samplePath(pair.rail1, 40)
      );
      if (quality !== null && quality < bestQuality && valid(pair)) {
        bestQuality = quality;
        chosen = {
          ...pair,
          method: "split-into-satin-columns",
          split: { t1, t2 },
          piecePath: piece.path,
        };
      }
    }
    if (!chosen)
      throw new Error(
        `The app's rail helper could not validate section ${piece.id}. No rails were replaced.`
      );
    pairs.push(chosen);
  }
  if (!pairs.length)
    throw new Error("Trace returned no validated satin columns.");
  return { ...result, pairs };
}

// Keep every usable section when a completed plan cannot be fully materialized.
// Failed outlines remain in the editable artwork and are recorded for review.
export function materializeAvailableRails(result) {
  const pairs = [];
  const issues = [...(result.issues || [])];
  const unresolvedPieces = [...(result.unresolvedPieces || [])];
  let rejected = 0;
  for (const piece of result.pieces || []) {
    if (isCollapsedSatinPiece(piece)) continue;
    try {
      pairs.push(...materializeVideoRails({ pieces: [piece] }).pairs);
    } catch (_) {
      rejected++;
      unresolvedPieces.push(piece);
      issues.push({
        sectionId: piece.id,
        message: "This section needs manual satin edges.",
        action: "Use Split into Satin Columns, Select Satin Columns, or Draw Edges to finish it.",
      });
    }
  }
  const unresolved = (Array.isArray(result.unresolved)
    ? result.unresolved.length : Number(result.unresolved) || 0) + rejected;
  return {
    ...result,
    pairs,
    complete: result.complete !== false && unresolved === 0 && pairs.length > 0,
    unresolved,
    unresolvedPieces,
    issues,
  };
}

// Disconnecting a view stops polling, not the durable backend job.
export function createAstraSatinClient() {
  const controller = new AbortController();
  let stopped = false,
    jobId = null,
    timer;
  const base = SATIN_API;
  // Bound each status request, including authentication and response-body reads.
  const readStatus = async (url) => {
    const requestController = new AbortController();
    const abort = () => requestController.abort();
    controller.signal.addEventListener("abort", abort);
    let deadline;
    try {
      return await Promise.race([
        (async () => {
          const response = await aiUsageFetch(url, { signal: requestController.signal });
          return { response, data: response.ok ? await response.json() : null };
        })(),
        new Promise((_, reject) => {
          deadline = setTimeout(() => {
            requestController.abort();
            reject(new Error("Status request timed out."));
          }, 30000);
        }),
      ]);
    } finally {
      clearTimeout(deadline);
      controller.signal.removeEventListener("abort", abort);
    }
  };
  const client = {
    terminate() {
      stopped = true;
      clearTimeout(timer);
      controller.abort();
    },
    async postMessage({
      contours,
      mmPerUnit,
      context = "unsaved",
      snapshot,
      resumeOnly = false,
      startedAt = Date.now(),
      processingMode = "standard",
      retryUnresolved = false,
    }) {
      try {
        let saved = await loadSatinJob(context);
        if (stopped) return;
        const matching = saved?.snapshot === snapshot && !saved?.consumed;
        if (resumeOnly && !matching) return;
        jobId = matching ? saved.id : crypto.randomUUID();
        const input = matching
          ? saved.input
          : {
              contours,
              mmPerUnit,
              profile: processingMode === "economy" ? "economy" : "thorough",
              ...(retryUnresolved ? { retryUnresolved: true } : {}),
            };
        // Save BEFORE submitting; the ID makes ambiguous submissions safe to retry.
        saved = {
          ...(matching ? saved : {}),
          id: jobId,
          input,
          snapshot,
          consumed: false,
          startedAt: matching ? saved.startedAt || startedAt : startedAt,
        };
        await saveSatinJob(context, saved);
        if (stopped) return;
        if (!resumeOnly && !matching) {
          const response = await aiUsageFetch(base + "/jobs", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: jobId, input }),
            signal: controller.signal,
          });
          if (!response.ok)
            throw new Error((await response.json().catch(() => null))?.error || "The backend could not accept this job.");
        }
        let unavailableSince = null;
        const uncertain = (message) => client.onmessage?.({
          data: { error: message, statusUncertain: true },
        });
        const poll = async () => {
          if (stopped) return;
          try {
            const { response, data } = await readStatus(base + "/jobs/" + jobId);
            if (stopped) return;
            if (response.status === 404) {
              if (Date.now() - saved.startedAt > 24 * 60 * 60 * 1000) {
                uncertain("Trace can no longer find this saved job. Your design is unchanged. Check its status again before starting another run.");
                return;
              }
              // A submit interrupted before reaching the server is retried idempotently.
              const submitted = await aiUsageFetch(base + "/jobs", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ id: jobId, input }),
                signal: controller.signal,
              });
              if (!submitted.ok) throw new Error((await submitted.json().catch(() => null))?.error || "Job submission failed.");
              timer = setTimeout(poll, 2000);
              return;
            }
            if (!response.ok) throw new Error("Job service unavailable.");
            unavailableSince = null;
            if (stopped) return;
            const referenceImage = data.referenceImage || data.result?.referenceImage;
            if (referenceImage && referenceImage !== saved.referenceImage) {
              saved = { ...saved, referenceImage };
              await saveSatinJob(context, saved);
              client.onmessage?.({ data: { referenceImage, progress: data.progress || "Preparing satin columns…" } });
            }
            if (data.state === "completed") {
              let result;
              try {
                try {
                  result = materializeVideoRails(data.result);
                } catch (originalError) {
                  const pieces = [],
                    cuts = [...(data.result.cuts || [])];
                  for (const piece of data.result.pieces || []) {
                    if (stopped) return;
                    if (isCollapsedSatinPiece(piece)) continue;
                    try {
                      materializeVideoRails({ pieces: [piece] });
                      pieces.push(piece);
                    } catch {
                      client.onmessage?.({
                        data: { progress: "Refining a satin section locally…" },
                      });
                      try {
                        const repair = await aiUsageFetch(base + "/repair-section", {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ piece }),
                          signal: controller.signal,
                        });
                        if (!repair.ok) throw originalError;
                        const alternatives =
                          (await repair.json()).alternatives || [];
                        const accepted = alternatives.find((candidate) => {
                          try {
                            materializeVideoRails(candidate);
                            return true;
                          } catch {
                            return false;
                          }
                        });
                        if (!accepted) throw originalError;
                        pieces.push(...accepted.pieces);
                        cuts.push(...accepted.cuts);
                      } catch (error) {
                        if (stopped || error.name === "AbortError") return;
                        // A failed local repair must not discard other sections.
                        pieces.push(piece);
                      }
                    }
                  }
                  result = materializeAvailableRails({
                    ...data.result,
                    pieces,
                    cuts,
                  });
                }
              } catch (error) {
                client.onmessage?.({ data: { error: error.message } });
                return;
              }
              if (stopped) return;
              const applied = client.onmessage?.({ data: { result } });
              // Delivery alone is not success: a view may have changed or failed to apply.
              if (applied === true)
                await saveSatinJob(context, { ...saved, consumed: true });
              return;
            }
            if (data.state === "failed" || data.state === "cancelled") {
              await saveSatinJob(context, { ...saved, consumed: true });
              client.onmessage?.({
                data: { error: data.error || "Job cancelled." },
              });
              return;
            }
            const progressKey = JSON.stringify([data.state, data.progress]);
            if (saved.progressKey !== progressKey) {
              saved = { ...saved, progressKey, lastProgressAt: Date.now() };
              await saveSatinJob(context, saved);
            }
            // Preserve the job ID and any eventual result; age alone cannot prove failure.
            if (Date.now() - saved.startedAt > 24.5 * 60 * 60 * 1000 ||
                (data.state !== "queued" && Date.now() - saved.lastProgressAt >= 30 * 60 * 1000)) {
              uncertain("Trace has stopped reporting progress. We cannot confirm that this job is still processing. Your design is unchanged. Check its status again to recover any finished result.");
              return;
            }
            client.onmessage?.({
              data: {
                progress:
                  data.progress ||
                  "Queued on the backend. You can leave this page.",
                referenceImage: data.referenceImage,
              },
            });
          } catch (error) {
            if (stopped) return;
            unavailableSince ??= Date.now();
            if (Date.now() - unavailableSince >= 5 * 60 * 1000) {
              uncertain("Trace’s status is unavailable. Your saved job has been kept. Check its status again when the connection returns.");
              return;
            }
            client.onmessage?.({
              data: {
                progress: "Reconnecting… Unable to confirm the job’s current status. Do not submit it again.",
              },
            });
          }
          if (!stopped) timer = setTimeout(poll, 2000);
        };
        await poll();
      } catch (error) {
        if (!stopped) client.onmessage?.({ data: { error: error.message } });
      }
    },
  };
  return client;
}
